import type { ComponentType, ReactNode, RefObject } from 'react'
import { Image, Pressable, View } from 'react-native'
import type { ImageSourcePropType } from 'react-native'
import { QrCode } from 'lucide-react-native'
import type { LucideProps } from 'lucide-react-native'
import { colors } from '@chillout/core'
import { StampCircle } from './StampCircle'
import { StampProgress } from './StampProgress'
import { StampGrid } from './StampGrid'

const STAMP_SIZE = 44

interface StampCardProps {
  current: number
  total: number
  logoSource: ImageSourcePropType
  logoAccessibilityLabel: string
  countText: string
  subtitleText: string
  accessibilityLabel?: string
  onPress?: () => void
  iconForPosition?: (position: number) => ComponentType<LucideProps> | undefined
  gap?: number
  maxGap?: number
  stampProgressRef?: RefObject<View | null>
  qrCodeRef?: RefObject<View | null>
}

export function StampCard({
  current,
  total,
  logoSource,
  logoAccessibilityLabel,
  countText,
  subtitleText,
  accessibilityLabel,
  onPress,
  iconForPosition,
  gap,
  maxGap,
  stampProgressRef,
  qrCodeRef,
}: StampCardProps) {
  const renderStamp = (i: number) => (
    <StampCircle key={i} state={i < current ? 'filled' : 'empty'} size={STAMP_SIZE} icon={iconForPosition?.(i + 1)} />
  )

  const content: ReactNode = (
    <>
      <View className="flex-row items-start justify-between">
        <Image
          source={logoSource}
          style={{ height: 36, width: 80, marginLeft: -12 }}
          resizeMode="contain"
          accessible
          accessibilityRole="image"
          accessibilityLabel={logoAccessibilityLabel}
        />
        <View ref={qrCodeRef}>
          <QrCode size={22} color={colors.text.inverse} strokeWidth={1.5} />
        </View>
      </View>

      {total > 0 ? (
        <StampGrid count={total} stampSize={STAMP_SIZE} renderStamp={renderStamp} gap={gap} maxGap={maxGap} />
      ) : null}

      <View ref={stampProgressRef}>
        <StampProgress current={current} total={total} countText={countText} subtitleText={subtitleText} variant="dark" />
      </View>
    </>
  )

  if (onPress) {
    return (
      <Pressable
        className="gap-6 rounded-xl bg-canvas-card-dark p-6"
        onPress={onPress}
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel}
      >
        {content}
      </Pressable>
    )
  }

  return (
    <View
      className="gap-6 rounded-xl bg-canvas-card-dark p-6"
      accessible={!!accessibilityLabel}
      accessibilityLabel={accessibilityLabel}
    >
      {content}
    </View>
  )
}
